import { useIsMobile } from '@epilot/concorde-elements'
import { clsx } from '@epilot/journey-logic-commons'
import type { Layout, LayoutProps } from '@jsonforms/core'
import { JsonFormsDispatch, withJsonFormsLayoutProps } from '@jsonforms/react'
import { AnimatePresence, useReducedMotion } from 'motion/react'

import { useJourneyContext } from '../../../blocks-renderers/providers'
import { useAnimationEffect } from '../../../blocks-renderers/utils/hooks/useAnimationEffects'
import { useStickyZone } from '../../utils/hooks/useStickyZone'
import { BlockRevealContainer, StickyZone, StepHeader } from '../components'

import classes from './GridLayout.module.scss'

const GridLayout = ({
  uischema,
  renderers,
  schema,
  path,
  enabled
}: LayoutProps) => {
  const { context } = useJourneyContext()

  const isPreview = context?._isPreview
  const currentStepIndex = context._navigationInfo.currentStepIndex
  const currentStep = context.journey.steps[currentStepIndex]

  const layout = uischema as Layout

  const hasRequiredField = Boolean(
    schema?.required && schema.required.length > 0
  )

  const isMobile = useIsMobile()
  const { sticky, hasShopCartSticky, stickyRef, stickyHeight } = useStickyZone(
    layout.elements,
    isMobile
  )

  const customStyle = {
    ...(isMobile ? { paddingBottom: sticky?.length ? stickyHeight : 0 } : {})
  }

  return (
    <div
      className={clsx('Concorde-Layout-Container', classes.gridLayoutContainer)}
      id={`Concorde-Step-Panel-${currentStepIndex + 1}`}
      role="tabpanel"
      style={customStyle}
    >
      <StepHeader
        className={classes.gridLayoutHeader}
        hasRequiredField={hasRequiredField}
        step={{ ...currentStep, stepIndex: currentStepIndex }}
      />

      <div
        className={clsx('Concorde-Layout-Content', classes.gridLayoutContent)}
      >
        <VisibleElements
          enabled={enabled}
          isActive={!isPreview}
          isMobile={isMobile}
          layout={layout}
          path={path}
          renderers={renderers}
          schema={schema}
        />

        {isMobile && sticky.length > 0 && (
          <StickyZone
            hasShopCartSticky={hasShopCartSticky}
            ref={stickyRef}
            stickyItemCount={sticky.length}
          >
            {sticky.map((child, index) => (
              <JsonFormsDispatch
                enabled={enabled}
                key={'footer' + index}
                path={path}
                renderers={renderers}
                schema={schema}
                uischema={child}
              />
            ))}
          </StickyZone>
        )}
      </div>
    </div>
  )
}

const VisibleElements = ({
  layout,
  isMobile,
  isActive,
  enabled,
  path,
  renderers,
  schema
}: Pick<LayoutProps, 'enabled' | 'path' | 'renderers' | 'schema'> & {
  layout: Layout
  isMobile: boolean
  isActive: boolean
}) => {
  const { animationType } = useAnimationEffect()
  const shouldReduceMotion = useReducedMotion()

  return (
    <AnimatePresence
      custom={shouldReduceMotion ? 'none' : animationType}
      mode="popLayout"
    >
      {layout.elements.map((child, index) => {
        // When display is undefined, it means that there's no display logic connected to it
        const isVisible =
          child?.options?.display === undefined ? true : child?.options?.display

        if (!isVisible || (child.options?.stickyOnMobile && isMobile)) {
          return null
        }

        return (
          <BlockRevealContainer
            animationType={animationType}
            blockType={child.type}
            child={child}
            isActive={isActive}
            isVisible={isVisible}
            key={`${index}-${child.type}`}
          >
            <JsonFormsDispatch
              enabled={enabled}
              key={`${index}-${child.type}`}
              path={path}
              renderers={renderers}
              schema={schema}
              uischema={child}
            />
          </BlockRevealContainer>
        )
      })}
    </AnimatePresence>
  )
}

export default withJsonFormsLayoutProps(GridLayout)
